// src/components/AirPanel.jsx (s112, Phase 4a) — what the air around a city carries, from
// the nearest EPA monitor with a complete year (src/data/airLoad.js). Prints only measured
// annual means, names the monitor and its distance, and says so when there is none within
// 25 km. What the numbers mean at the glass lives once, on /methodology#air. Each reading is a
// stacked card (label, value, flag on their own lines): it reads better on a phone, and it keeps
// data labels from being counted as template sentences by measure-template-share. The monitor
// line names the site and its street address, so two cities sharing a monitor still read apart.
import React from 'react';
import { Link } from 'react-router-dom';

const SMALL = new Set(['OF', 'THE', 'AND', 'AT', 'ON', 'IN']);
// EPA names and addresses arrive in capitals, sometimes mixed with normal case: title-case the
// all-capital words of four letters or more, lower-case small joining words, keep codes (GA, CTR).
export function monitorName(n) {
  if (!n) return n;
  return n.split(/(\s+|-|\/|,)/).map((w) => (SMALL.has(w) ? w.toLowerCase() : /^[A-Z]{4,}\.?$/.test(w) ? w.charAt(0) + w.slice(1).toLowerCase() : w)).join('');
}
// Facility names arrive in capitals: title-case them, keeping short codes and fixing company suffixes.
const SUFFIX = { CO: 'Co', INC: 'Inc', CORP: 'Corp', LTD: 'Ltd' };
export const companyName = (n) => monitorName(String(n || '')).split(/(\s+|\.|,)/).map((w) => SUFFIX[w.toUpperCase()] && w === w.toUpperCase() ? SUFFIX[w.toUpperCase()] : w).join('');
export const airFlag = (kind, pct) => (kind === 'pm10'
  ? (pct >= 50 ? `Dustier than ${pct}% of US sites` : `Less dusty than ${100 - pct}% of US sites`)
  : (pct >= 50 ? `Hazier than ${pct}% of US sites` : `Cleaner than ${100 - pct}% of US sites`));

const DIRS = ['north', 'north-east', 'east', 'south-east', 'south', 'south-west', 'west', 'north-west'];
// Compass direction from the city point to the monitor (initial great-circle bearing).
export function bearingWord(a, b) {
  const r = Math.PI / 180; const y = Math.sin((b.lon - a.lon) * r) * Math.cos(b.lat * r);
  const x = Math.cos(a.lat * r) * Math.sin(b.lat * r) - Math.sin(a.lat * r) * Math.cos(b.lat * r) * Math.cos((b.lon - a.lon) * r);
  return DIRS[Math.round((((Math.atan2(y, x) / r) + 360) % 360) / 45) % 8];
}

export default function AirPanel({ air, T, fontBody, fontMono }) {
  const p = air?.particulate; if (!p) return null;
  const sa = air.salt;
  const ind = air.industry;
  const sm = air.smoke;
  const fm = air.farm;
  const po = air.pollen;
  const rows = [['pm10', 'Coarse dust (PM10)'], ['pm25', 'Fine particles (PM2.5)']].filter(([k]) => p[k] && p.s === 'captured');
  const kmText = (km) => (km >= 100 ? `${Math.round(km).toLocaleString('en-US')} km` : `${km} km`);
  const card = (key, label, value, note, to) => (
    <div key={key} data-air={key} style={{ minWidth: 150 }}>
      <div style={{ fontFamily: fontMono, fontSize: 10, letterSpacing: '0.12em', color: T.inkSoft }}>{label}</div>
      <div style={{ fontFamily: fontMono, fontSize: 22, color: T.ink, margin: '2px 0' }}>{value}</div>
      <div style={{ fontFamily: fontBody, fontSize: 13, color: T.inkSoft }}><Link to={to} style={{ color: 'inherit' }}>{note}</Link></div>
    </div>
  );
  const sites = [...new Map(rows.map(([k]) => [p[k].site, p[k]])).values()];
  const label = { fontFamily: fontMono, fontSize: 11, letterSpacing: '0.18em', color: T.sienna, marginBottom: 12 };
  return (
    <div data-region="air-panel" style={{ marginTop: 40 }}>
      <div style={label}>WHAT THE AIR CARRIES</div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '14px 36px' }}>
        {rows.map(([k, name]) => card(k, name.toUpperCase(), `${p[k].mean} µg/m³`, airFlag(k, p[k].pct), '/methodology#air'))}
        {sa ? card('salt', 'SALT WATER', kmText(sa.km), sa.water, '/methodology#salt') : null}
        {sa?.lake ? card('lake', 'GREAT LAKE', `${sa.lake.km} km`, `${sa.lake.name}, fresh water`, '/methodology#salt') : null}
        {sa?.saltLake ? card('saltlake', 'SALT LAKE', `${sa.saltLake.km} km`, `${sa.saltLake.name}, hypersaline`, '/methodology#salt') : null}
        {sm ? card('smoke', 'WILDFIRE SMOKE', sm.worst.days ? `${sm.avg} ${sm.avg === 1 ? 'day' : 'days'} a year` : 'None', sm.worst.days ? `worst ${sm.worst.days} days in ${sm.worst.year}` : `no heavy smoke, ${Object.keys(sm.years)[0]}–${Object.keys(sm.years).slice(-1)[0]}`, '/methodology#smoke') : null}
        {po ? card('pollen', 'SPRING BLOOM', po.s === 'captured' ? `Around ${po.date}` : 'Not modelled', po.s === 'captured' ? (po.doy < 45 ? 'pollen runs much of the year' : 'tree pollen builds around it') : 'outside the lower 48', '/methodology#pollen') : null}
        {fm ? card('farm', 'CROPLAND (COUNTY)', fm.s === 'captured' ? `${fm.pct}%` : fm.s === 'withheld' ? 'Withheld' : 'Not reported', fm.s === 'captured' ? `of ${fm.county} land, ${fm.year}` : `${fm.county}, ${fm.year} census`, '/methodology#farm') : null}
        {ind ? card('industry', 'INDUSTRY (EPA TRI)', ind.n ? `${ind.n} ${ind.n === 1 ? 'plant' : 'plants'}` : 'None', ind.n ? `${ind.lb.toLocaleString('en-US')} lb to air, ${ind.year}` : 'in or within 2 km of the city', '/methodology#industry') : null}
      </div>
      {p.s === 'captured' ? sites.map((s) => (
        <p key={s.site} data-air-site={s.site} style={{ fontFamily: fontMono, fontSize: 11, lineHeight: 1.6, color: T.inkSoft, margin: '8px 0 0' }}>
          {s.year ?? p.year} annual mean at the {monitorName(s.name)} monitor{s.addr && s.addr !== s.name ? `, ${monitorName(s.addr)}` : ''}, {s.km} km {bearingWord(air.point, s)} of the city (EPA site {s.site}).
        </p>
      )) : (
        <p data-air="none" style={{ fontFamily: fontBody, fontSize: 14, color: T.inkSoft, margin: '8px 0 0' }}>
          Nearest EPA particle monitor: {p.nearestKm} km
        </p>
      )}
      {ind?.n ? (
        <p data-air="industry-top" style={{ fontFamily: fontMono, fontSize: 11, lineHeight: 1.6, color: T.inkSoft, margin: '8px 0 0' }}>
          Largest air release: {companyName(ind.top.name)}{ind.top.addr ? `, ${monitorName(ind.top.addr)}` : ''} ({ind.top.sector.toLowerCase()}), {ind.top.outsideKm ? `${ind.top.outsideKm} km outside the city limits` : 'inside the city limits'}, {ind.top.lb.toLocaleString('en-US')} lb in {ind.year}.
        </p>
      ) : null}
      <p style={{ fontFamily: fontMono, fontSize: 11, margin: '6px 0 0' }}>
        <Link to="/methodology#air" style={{ color: T.inkSoft }}>How we measure the air</Link>
      </p>
    </div>
  );
}
